import { resetDirty } from "@frui.ts/dirtycheck";
import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation";
import { Modules } from "config";
import download from "downloadjs";
import CommissionForInvoiceResponse from "entities/commissionForInvoiceResponse";
import Distributor from "entities/distributor";
import type PayGateTransactionsFilter from "entities/filters/payGateTransactionsFilter";
import GetCommissionsForInvoiceRequest, {
  CommissionsForInvoiceFiltersValidationRules,
} from "entities/getCommissionsForInvoiceRequest";
import { computed, observable } from "mobx";
import CommissionsForInvoiceRepository from "repositories/commissionsForInvoiceRepository";
import ConfirmationService from "services/confirmationService";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import MonthOptionsService from "services/monthOptionsService";
import NotificationService from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import { isFilterEmpty } from "utils/filters";
import { findMonthOptionByDate } from "utils/monthOption";
import ListViewModel from "../../../listViewModel";

const navigationName = "commissions-for-invoice";

@Router.registerRoute({ name: Router.Self, route: navigationName })
export default class CommissionsForInvoiceViewModel extends ListViewModel<
  CommissionForInvoiceResponse,
  ScreenBase,
  ScreenBase,
  GetCommissionsForInvoiceRequest
> {
  readonly modelName = "commissionForInvoice";
  @observable downloadInProgress: Map<string, boolean> = new Map();

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: CommissionsForInvoiceRepository,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    private optionsService: OptionsService,
    private monthOptionsService: MonthOptionsService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  onInitialize() {
    this.initFilter();
    super.onInitialize();
  }

  @computed
  get startOfMonthOptions() {
    return this.monthOptionsService.startOfMonthOptions;
  }

  @computed
  get endOfMonthOptions() {
    return this.monthOptionsService.endOfMonthOptions;
  }

  @computed
  get allowWrite() {
    return true;
  }

  @computed
  get allowDelete() {
    return false;
  }

  @bound
  allowItemDetail(item: any) {
    return false;
  }

  @bound
  async onDownloadClick(item: CommissionForInvoiceResponse) {
    try {
      this.downloadInProgress.set(item.id, true);
      const response = await this.repository.getCommissionsForInvoiceDownload(item.id);
      this.downloadInProgress.set(item.id, false);
      download(response.data, `${item.id}.xlsx`);
    } catch (e) {}
  }

  protected createFilter() {
    return attachAutomaticValidator(new GetCommissionsForInvoiceRequest(), CommissionsForInvoiceFiltersValidationRules);
  }

  protected resetFilterValues(filter: GetCommissionsForInvoiceRequest) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);
  }

  // Method overload to prevent validate filters when are empty
  applyFilter() {
    if (!isFilterEmpty(this.filter) && !validate(this.filter)) {
      return false;
    }

    this.appliedFilter = this.cloneFilterForApply(this.filter);
    this.pagingFilter.offset = 0;
    resetDirty(this.filter);
    return true;
  }

  protected fetchData() {
    if (!isFilterEmpty(this.appliedFilter)) {
      const { limit, offset } = this.pagingFilter;
      return this.repository.getAllCommissionsForInvoices({ limit, offset }, this.appliedFilter);
    } else {
      return Promise.resolve({
        metadata: {
          total: 0,
          offset: 0,
          limit: 0,
        },
        items: [],
      });
    }
  }

  protected parseFilterParam(paramName: keyof PayGateTransactionsFilter | string, value: any) {
    switch (paramName) {
      case "sinceDate":
        return findMonthOptionByDate(value, this.startOfMonthOptions)?.id;
      case "untilDate":
        return findMonthOptionByDate(value, this.endOfMonthOptions)?.id;
    }
    return super.parseFilterParam(paramName, value);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `commissionForInvoice.${code}`, size);
  }
}
